import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    TextField,
    Button,
    Grid,
    Checkbox,
    FormControlLabel,
    CircularProgress,
    Alert,
    Typography,
    Box,
    FormGroup,
    FormLabel,
    Stack,
    Divider,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    Autocomplete,
    Paper,
    ListItem,
    ListItemText,
    Tooltip,
    IconButton,
    Chip
} from '@mui/material';
import { createCustomer, updateCustomer } from '../services/customerService';
import apiClient from '../api';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import debounce from 'lodash/debounce';

// Time options for dropdowns
const timeOptions = [
    '12:00 AM', '12:30 AM', '1:00 AM', '1:30 AM', '2:00 AM', '2:30 AM', '3:00 AM', '3:30 AM',
    '4:00 AM', '4:30 AM', '5:00 AM', '5:30 AM', '6:00 AM', '6:30 AM', '7:00 AM', '7:30 AM',
    '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM', '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM', '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM', '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
    '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM'
];

// Helper to initialize day hours
const initializeDayHours = () => ({
    monday: { open: '', close: '' },
    tuesday: { open: '', close: '' },
    wednesday: { open: '', close: '' },
    thursday: { open: '', close: '' },
    friday: { open: '', close: '' },
    saturday: { open: '', close: '' },
    sunday: { open: '', close: '' }
});

const initialFormData = {
    name: '',
            contactPerson: '',
        contactPhone: '',
        address: {
        street: '',
        city: '',
        state: '',
        zip: ''
    },
    openHours: '',
    openDays: {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false
    },
    dayHours: initializeDayHours(),
    openTimeStart: '',
    openTimeEnd: '',
    avgUnloadTimeMinutes: '',
    overnightParking: false,
    lumperFee: false,
    detentionPolicy: '',
    notes: ''
};

// Form states
const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
];

function CustomerForm({ open, onClose, customer, onSave }) {
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState('');
    const isEditMode = Boolean(customer);
    
    // Address autocomplete state
    const [existingAddresses, setExistingAddresses] = useState([]);
    const [googleMapsAddresses, setGoogleMapsAddresses] = useState([]);
    const [addressesLoading, setAddressesLoading] = useState(false);
    const [streetInputValue, setStreetInputValue] = useState('');
    const [selectedFullAddress, setSelectedFullAddress] = useState(null);
    const [addressSearchType, setAddressSearchType] = useState('googlemaps'); // 'googlemaps' or 'existing'
    const [addressOpen, setAddressOpen] = useState(false);

    // Debounced search for Google Maps
    const fetchGoogleMapsAddresses = useCallback(
        debounce(async (query) => {
            if (!query || query.length < 3) {
                // Keep previous results visible; just stop loading
                setAddressesLoading(false);
                return;
            }
            setAddressesLoading(true);
            try {
                const response = await apiClient.get(`/customers/googlemaps-search`, {
                    params: { query }
                });
                if (response.data && response.data.data) {
                    setGoogleMapsAddresses(response.data.data);
                }
            } catch (err) {
                if (err?.response?.status !== 429) console.error('Error fetching Google Maps addresses:', err);
            } finally {
                setAddressesLoading(false);
            }
        }, 600),
        []
    );

    useEffect(() => {
        if (isEditMode && customer) {
            // Parse existing openHours value to populate day checkboxes if needed
            const updatedCustomer = { ...initialFormData, ...customer };
            
            // Initialize dayHours
            updatedCustomer.dayHours = initializeDayHours();

            // If customer has the old format openHours but not the new openDays structure
            if (customer.openHours && !customer.openDays) {
                const dayAbbreviations = {
                    'M': 'monday',
                    'Mon': 'monday',
                    'T': 'tuesday',
                    'Tue': 'tuesday',
                    'W': 'wednesday',
                    'Wed': 'wednesday',
                    'Th': 'thursday',
                    'Thu': 'thursday',
                    'F': 'friday',
                    'Fri': 'friday',
                    'S': 'saturday',
                    'Sat': 'saturday',
                    'Su': 'sunday',
                    'Sun': 'sunday'
                };
                
                // Initialize openDays object
                updatedCustomer.openDays = { ...initialFormData.openDays };
                
                // Try to extract time values
                const timeRegex = /(\d{1,2}(?::\d{2})?(?:am|pm)?)\s*-\s*(\d{1,2}(?::\d{2})?(?:am|pm)?)/i;
                const timeMatch = customer.openHours.match(timeRegex);
                
                if (timeMatch) {
                    updatedCustomer.openTimeStart = timeMatch[1];
                    updatedCustomer.openTimeEnd = timeMatch[2];
                    
                    // Set this as default time for all days
                    Object.keys(updatedCustomer.dayHours).forEach(day => {
                        updatedCustomer.dayHours[day] = {
                            open: timeMatch[1],
                            close: timeMatch[2]
                        };
                    });
                }
                
                // Check for day abbreviations to set corresponding checkboxes
                Object.entries(dayAbbreviations).forEach(([abbr, day]) => {
                    if (customer.openHours.includes(abbr)) {
                        updatedCustomer.openDays[day] = true;
                    }
                });
            } else if (customer.dayHours) {
                // If customer already has dayHours data
                updatedCustomer.dayHours = customer.dayHours;
            }
            
            setFormData(updatedCustomer);
            if (updatedCustomer.address && updatedCustomer.address.street) {
                setStreetInputValue(updatedCustomer.address.street);
            }
        } else {
            setFormData(initialFormData);
            setStreetInputValue('');
        }
        // Reset error when dialog opens or customer changes
        setError(null);
    }, [customer, isEditMode, open]); // Depend on `open` to reset form when re-opened for Add

    // Fetch all unique addresses when the dialog opens
    useEffect(() => {
        const fetchExistingAddresses = async () => {
            if (!open) return;
            
            setAddressesLoading(true);
            try {
                // Use the API client to fetch all unique addresses
                const response = await apiClient.get('/customers/addresses');
                if (response.data && response.data.data) {
                    setExistingAddresses(response.data.data);
                }
            } catch (err) {
                console.error('Error fetching addresses:', err);
                // Don't show an error to user as this is an enhancement, not critical
            } finally {
                setAddressesLoading(false);
            }
        };

        fetchExistingAddresses();
    }, [open]);

      // Update Google Maps search when street input changes
    useEffect(() => {
        if (addressSearchType === 'googlemaps') {
            if (streetInputValue.length >= 3) {
                setAddressOpen(true);
                fetchGoogleMapsAddresses(streetInputValue);
            } else {
                // Close suggestions when less than 3 chars
                setAddressOpen(false);
            }
        }
    }, [streetInputValue, addressSearchType, fetchGoogleMapsAddresses]);

    const handleChange = (event) => {
        const { name, value, type, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleAddressChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                [name]: value
            }
        }));
    };

    const handleAddressSelect = (event, newValue) => {
        if (!newValue) return;
        
        console.log('Selected address:', newValue);
        
        // If an existing address is selected, populate all address fields
        setSelectedFullAddress(newValue);
        setAddressOpen(false);
        
        // Process the state value to ensure it's the correct format for the dropdown
        let stateValue = newValue.state || '';
        let zipValue = newValue.zip || '';
        
        // Sometimes the ZIP is included in the state field (e.g., "Missouri 63129")
        // Extract ZIP from state if zip field is empty
        if (!zipValue && stateValue) {
            const zipInStateMatch = stateValue.match(/\b(\d{5}(-\d{4})?)\b/);
            if (zipInStateMatch) {
                zipValue = zipInStateMatch[1];
                // Remove ZIP from state value
                stateValue = stateValue.replace(zipValue, '').trim();
            }
        }
        
        // Check if the state is in the correct format (2-letter code)
        // If it's a full state name, try to convert it to the code
        if (stateValue.length > 2) {
            const stateOption = stateOptions.find(
                option => option.label.toLowerCase() === stateValue.toLowerCase()
            );
            if (stateOption) {
                stateValue = stateOption.value;
            } else {
                // If it's not found, try to find a state that includes this text
                const matchingState = stateOptions.find(
                    option => stateValue.toLowerCase().includes(option.label.toLowerCase()) || 
                             option.label.toLowerCase().includes(stateValue.toLowerCase())
                );
                if (matchingState) {
                    stateValue = matchingState.value;
                }
            }
        }
        
        // If ZIP has extra parts (like ZIP+4), just keep the first 5 digits
        if (zipValue.includes('-')) {
            zipValue = zipValue.split('-')[0];
        }
        
        setFormData(prev => ({
            ...prev,
            address: {
                street: newValue.street || '',
                city: newValue.city || '',
                state: stateValue,
                zip: zipValue,
                coordinates: newValue.coordinates || null
            }
        }));
    };

    const handleStreetInputChange = (event, newInputValue) => {
        setStreetInputValue(newInputValue);
        
        // When input is cleared or changed, update just the street field
        if (selectedFullAddress && newInputValue !== selectedFullAddress.street) {
            setSelectedFullAddress(null);
            // Reopen suggestions when user starts typing again
            if (newInputValue.length >= 3 && addressSearchType === 'googlemaps') {
                setAddressOpen(true);
            }
        }
        
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                street: newInputValue
            }
        }));
    };

    const handleDayChange = (event) => {
        const { name, checked } = event.target;
        setFormData(prev => ({
            ...prev,
            openDays: {
                ...prev.openDays,
                [name]: checked
            }
        }));
    };

    const handleDayHoursChange = (day, timeType, value) => {
        setFormData(prev => ({
            ...prev,
            dayHours: {
                ...prev.dayHours,
                [day]: {
                    ...prev.dayHours[day],
                    [timeType]: value
                }
            }
        }));
    };

    const handleNumberChange = (event) => {
        const { name, value } = event.target;
        // Allow empty string or convert to number
        setFormData(prev => ({
            ...prev,
            [name]: value === '' ? '' : Number(value)
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(''); // Clear previous success messages

        // Format the hours based on the day checkboxes and time selections
        let hoursString = '';
        const selectedDays = Object.entries(formData.openDays)
            .filter(([_, selected]) => selected)
            .map(([day]) => day);
        
        if (selectedDays.length > 0) {
            // Group days with the same hours
            const hourGroups = {};
            
            selectedDays.forEach(day => {
                const open = formData.dayHours[day].open || formData.openTimeStart;
                const close = formData.dayHours[day].close || formData.openTimeEnd;
                const timeKey = `${open}-${close}`;
                
                if (!hourGroups[timeKey]) {
                    hourGroups[timeKey] = [];
                }
                hourGroups[timeKey].push(day);
            });
            
            // Format each group
            const formattedGroups = Object.entries(hourGroups).map(([timeKey, days]) => {
                const [open, close] = timeKey.split('-');
                if (!open || !close) return '';
                
                // Format day abbreviations
                const dayAbbrevs = days.map(day => day.charAt(0).toUpperCase() + day.slice(1, 3)).join('-');
                return `${open}-${close} ${dayAbbrevs}`;
            });
            
            hoursString = formattedGroups.filter(Boolean).join('; ');
        }

        // Fallback to general hours if no specific day format was created
        if (!hoursString) {
            const timeRange = formData.openTimeStart && formData.openTimeEnd 
                ? `${formData.openTimeStart}-${formData.openTimeEnd}` 
                : '';
            
            hoursString = formData.openHours || timeRange;
        }

        // Convert empty string number fields to null or handle as needed by backend
        const dataToSubmit = {
            ...formData,
            openHours: hoursString,
            avgUnloadTimeMinutes: formData.avgUnloadTimeMinutes === '' ? null : formData.avgUnloadTimeMinutes,
        };

        try {
            console.log('[CustomerForm] Submitting customer data:', dataToSubmit);
            
            if (isEditMode) {
                const response = await updateCustomer(customer._id, dataToSubmit);
                console.log('[CustomerForm] Customer updated successfully:', response.data);
                setSuccess('Customer updated successfully!');
            } else {
                const response = await createCustomer(dataToSubmit);
                console.log('[CustomerForm] Customer created successfully:', response.data);
                setSuccess('Customer created successfully!');
            }
            
            // Notify parent component to refresh data
            if (onSave) {
                console.log('[CustomerForm] Calling onSave callback');
                onSave();
            }
            
            // Show success message but don't auto-close immediately
            // Let the user see the success message
            setTimeout(() => {
                setSuccess('');
            }, 3000);
            
        } catch (err) {
            console.error('Submit error:', err);
            const errorMessage = err.response?.data?.message || 
                               err.response?.data?.errors?.[0] || 
                               `Failed to ${isEditMode ? 'update' : 'create'} customer.`;
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData(initialFormData); // Reset form on close
        setError(null);
        setLoading(false);
        setSuccess(''); // Clear success message on close
        setSelectedFullAddress(null);
        setStreetInputValue('');
        setGoogleMapsAddresses([]);
        onClose();
    };

    // Copy time from one day to all selected days
    const copyTimeToSelectedDays = (sourceDay) => {
        const sourceOpen = formData.dayHours[sourceDay].open;
        const sourceClose = formData.dayHours[sourceDay].close;
        
        const updatedDayHours = { ...formData.dayHours };
        
        Object.entries(formData.openDays).forEach(([day, isSelected]) => {
            if (isSelected && day !== sourceDay) {
                updatedDayHours[day] = {
                    open: sourceOpen,
                    close: sourceClose
                };
            }
        });
        
        setFormData(prev => ({
            ...prev,
            dayHours: updatedDayHours
        }));
    };

    // Toggle between Google Maps and existing addresses
    const handleToggleAddressSearch = () => {
        setAddressSearchType(prev => prev === 'googlemaps' ? 'existing' : 'googlemaps');
        // Clear and close when toggling sources
        setGoogleMapsAddresses([]);
        setAddressOpen(false);
    };

    // Get all address suggestions combining Google Maps and existing addresses
    const getAddressSuggestions = () => {
        if (addressSearchType === 'googlemaps') {
            return googleMapsAddresses;
        } else {
            // For existing addresses
            return existingAddresses.map(address => ({
                ...address,
                label: `${address.street}, ${address.city}, ${address.state} ${address.zip}`
            }));
        }
    };

    // Custom rendering of address options in autocomplete
    const renderAddressOption = (props, option) => {
        return (
            <ListItem {...props} divider>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                    <Box sx={{ mr: 2, color: 'text.secondary', mt: 0.5 }}>
                        {addressSearchType === 'googlemaps' ? (
                            <LocationOnIcon fontSize="small" color="primary" />
                        ) : (
                            <HistoryIcon fontSize="small" color="action" />
                        )}
                    </Box>
                    <ListItemText
                        primary={
                            <Typography variant="body1" noWrap>
                                {option.street}
                            </Typography>
                        }
                        secondary={
                            <Typography variant="body2" color="text.secondary" noWrap>
                                {option.city}, {option.state} {option.zip}
                            </Typography>
                        }
                    />
                </Box>
            </ListItem>
        );
    };

    // Custom rendering of the input for autocomplete
    const renderAddressInput = (params) => (
        <TextField
            {...params}
            margin="dense"
            label="Street Address"
            placeholder={addressSearchType === 'googlemaps' ? "Type to search US addresses" : "Select from existing addresses"}
            fullWidth
            variant="outlined"
            InputProps={{
                ...params.InputProps,
                startAdornment: (
                    <InputAdornment position="start">
                        <Tooltip title={addressSearchType === 'googlemaps' ? "Searching US addresses" : "Showing existing addresses"}>
                            <IconButton 
                                onClick={handleToggleAddressSearch} 
                                size="small"
                                color={addressSearchType === 'googlemaps' ? 'primary' : 'default'}
                            >
                                {addressSearchType === 'googlemaps' ? <LocationOnIcon /> : <HistoryIcon />}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
                endAdornment: (
                    <>
                        {addressesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                    </>
                ),
            }}
        />
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', boxShadow: (t) => t.shadows[10] } }}>
            <DialogTitle sx={{ p: 2, background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white' }}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: '10px', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <BusinessIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            {isEditMode ? 'Edit Customer' : 'Add New Customer'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Capture key details to streamline deliveries and service.
                        </Typography>
                    </Box>
                </Stack>
            </DialogTitle>
            <form id="customer-form" onSubmit={handleSubmit}>
                <DialogContent sx={{ p: 2.5, bgcolor: '#fafafa', maxHeight: 'calc(100vh - 220px)', overflowY: 'auto', pb: 8 }} data-usage-screen="customers.edit">
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    {/* <DialogContentText>
                        Please fill in the customer details below.
                    </DialogContentText> */}
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Basic Info */}
                        <Grid item xs={12}>
                          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: (theme) => `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Basic Information</Typography>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField autoFocus={!isEditMode} margin="dense" name="name" label="Customer Name" type="text" fullWidth variant="outlined" size="small" value={formData.name} onChange={handleChange} required />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField margin="dense" name="contactPerson" label="Contact Person" type="text" fullWidth variant="outlined" size="small" value={formData.contactPerson} onChange={handleChange} />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField margin="dense" name="contactPhone" label="Contact Phone" type="tel" fullWidth variant="outlined" size="small" value={formData.contactPhone} onChange={handleChange} />
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>


                        {/* Address */}
                        <Grid item xs={12}>
                          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: (theme) => `1px solid ${theme.palette.divider}` }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Address</Typography>
                              <Chip size="small" icon={<LocationOnIcon />} label={addressSearchType === 'googlemaps' ? 'Google Maps' : 'Existing'} />
                            </Stack>
                            <Autocomplete
                                freeSolo
                                options={getAddressSuggestions()}
                                getOptionLabel={(option) => typeof option === 'string' ? option : option.label || ''}
                                inputValue={streetInputValue}
                                onInputChange={handleStreetInputChange}
                                onChange={handleAddressSelect}
                                loading={addressesLoading}
                                open={addressOpen}
                                onOpen={() => setAddressOpen(true)}
                                onClose={() => setAddressOpen(false)}
                                openOnFocus
                                filterOptions={(x) => x}
                                loadingText="Searching addresses..."
                                noOptionsText={
                                    addressSearchType === 'googlemaps' 
                                        ? streetInputValue.length < 3 
                                            ? "Type at least 3 characters to search" 
                                            : "No addresses found"
                                        : "No existing addresses found"
                                }
                                renderInput={renderAddressInput}
                                renderOption={renderAddressOption}
                                PaperComponent={(props) => (
                                    <Paper {...props} elevation={4} sx={{ mt: 0.5 }} />
                                )}
                            />
                            <Grid container spacing={2} sx={{ mt: 0.5 }}>
                              <Grid item xs={12} sm={6}>
                                <TextField margin="dense" name="city" label="City" type="text" fullWidth variant="outlined" size="small" value={formData.address.city} onChange={handleAddressChange} />
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <FormControl fullWidth margin="dense" size="small">
                                    <InputLabel id="state-label">State</InputLabel>
                                    <Select labelId="state-label" name="state" value={formData.address.state} onChange={handleAddressChange} label="State">
                                        <MenuItem value=""><em>Select State</em></MenuItem>
                                        {stateOptions.map(option => (
                                            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={3}>
                                <TextField margin="dense" name="zip" label="Zip Code" type="text" fullWidth variant="outlined" size="small" value={formData.address.zip} onChange={handleAddressChange} />
                              </Grid>
                            </Grid>
                          </Box>
                        </Grid>
                        {/* Removed legacy duplicate address fields */}

                        {/* Operational Details */}
                        <Grid item xs={12}>
                          <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: (theme) => `1px solid ${theme.palette.divider}` }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Operational Details</Typography>
                        
                        {/* Days of Week with Time Dropdowns */}
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ mb: 1 }}>Operating Hours</FormLabel>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {Object.entries(formData.openDays).map(([day, isChecked]) => (
                                    <Box 
                                        key={day} 
                                        sx={{ 
                                            display: 'flex', 
                                            flexDirection: { xs: 'column', sm: 'row' }, 
                                            alignItems: { xs: 'flex-start', sm: 'center' },
                                            gap: 2,
                                            p: 1,
                                            borderRadius: 1,
                                            bgcolor: isChecked ? 'action.hover' : 'transparent',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox 
                                                    checked={isChecked} 
                                                    onChange={handleDayChange} 
                                                    name={day}
                                                />
                                            }
                                            label={day.charAt(0).toUpperCase() + day.slice(1)}
                                            sx={{ minWidth: { xs: '100%', sm: 120 } }}
                                        />
                                        <Collapse in={isChecked} sx={{ width: { xs: '100%', sm: 'auto' }, flex: 1 }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                flexDirection: { xs: 'column', sm: 'row' }, 
                                                gap: 2,
                                                alignItems: { xs: 'flex-start', sm: 'center' },
                                                width: '100%'
                                            }}>
                                                <FormControl variant="outlined" size="small" sx={{ minWidth: 120, flex: 1 }}>
                                                    <InputLabel id={`${day}-open-label`}>Open</InputLabel>
                                                    <Select
                                                        labelId={`${day}-open-label`}
                                                        value={formData.dayHours[day].open}
                                                        onChange={(e) => handleDayHoursChange(day, 'open', e.target.value)}
                                                        label="Open"
                                                    >
                                                        <MenuItem value=""><em>Select Time</em></MenuItem>
                                                        {timeOptions.map(time => (
                                                            <MenuItem key={`${day}-open-${time}`} value={time}>{time}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <Typography variant="body1" sx={{ display: { xs: 'none', sm: 'block' } }}>to</Typography>
                                                <FormControl variant="outlined" size="small" sx={{ minWidth: 120, flex: 1 }}>
                                                    <InputLabel id={`${day}-close-label`}>Close</InputLabel>
                                                    <Select
                                                        labelId={`${day}-close-label`}
                                                        value={formData.dayHours[day].close}
                                                        onChange={(e) => handleDayHoursChange(day, 'close', e.target.value)}
                                                        label="Close"
                                                    >
                                                        <MenuItem value=""><em>Select Time</em></MenuItem>
                                                        {timeOptions.map(time => (
                                                            <MenuItem key={`${day}-close-${time}`} value={time}>{time}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                {day === 'monday' && isChecked && (
                                                    <Button 
                                                        size="small" 
                                variant="outlined"
                                                        onClick={() => copyTimeToSelectedDays('monday')}
                                                        sx={{ ml: 'auto' }}
                                                    >
                                                        Copy to Selected Days
                                                    </Button>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </Box>
                                ))}
                            </Box>
                        </Grid>
                        
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                name="avgUnloadTimeMinutes"
                                label="Avg. Unload Time (Minutes)"
                                type="number"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.avgUnloadTimeMinutes}
                                onChange={handleNumberChange}
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            {/* Keep checkboxes on same line */}
                            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.overnightParking}
                                            onChange={handleChange}
                                            name="overnightParking"
                                            color="primary"
                                        />
                                    }
                                    label="Overnight Parking Available"
                                />
                             <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.lumperFee}
                                            onChange={handleChange}
                                            name="lumperFee"
                                            color="primary"
                                        />
                                    }
                                    label="Lumper Fee Required"
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                margin="dense"
                                name="detentionPolicy"
                                label="Detention Policy"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.detentionPolicy}
                                onChange={handleChange}
                                placeholder="e.g., 2 hours free, $50/hr after"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                name="notes"
                                label="Notes"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.notes}
                                onChange={handleChange}
                                multiline
                                rows={3}
                            />
                        </Grid>
                          </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, gap: 1.5, position: 'sticky', bottom: 0, bgcolor: 'background.paper' }}>
                    <Button onClick={handleClose} disabled={loading} variant="outlined">Cancel</Button>
                    <Button type="submit" form="customer-form" variant="contained" disabled={loading} sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }} startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}>
                        {loading ? 'Saving...' : isEditMode ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default CustomerForm; 